import { api, API_URL, getToken } from '../config/api';

// ---- Auth ----
// Same Outfino auth flow as the partner portal / admin / mobile app: POST /auth
// with { email, password } → { token, user }. Social (Google/Apple) tokens are
// handled inline on the Login page and persisted via AuthContext.loginWithToken.
export async function login(email, password) {
  const res = await api('/auth', { method: 'POST', body: { email, password } });
  return { token: res.token, user: res.user };
}

export async function fetchMe() {
  // API responds `{ message: { user, validationRequired } }`; api() unwraps the
  // `message`, so we take `.user`.
  const res = await api('/user/me');
  return res?.user ?? res;
}

// ============================================================================
// CREATOR ENDPOINTS — live on the dev API (https://dev.api.outfino.io/v3).
// API_URL already ends in /v3, so paths here are WITHOUT a second /v3.
// The api() helper unwraps the `{ data, success }` and `{ message }` envelopes.
// ============================================================================

// GET /creators/me → the current user's creator profile:
//   { status: 'none' | 'pending' | 'approved' | 'rejected',
//     tiktok, instagram, note, tiktokFollowers, instagramFollowers }
// status 'none' = the user has not applied yet.
export async function fetchCreatorMe() {
  return api('/creators/me');
}
// Back-compat alias for older callers.
export const fetchCreatorProfile = fetchCreatorMe;

// POST /creators/apply  body { tiktok, instagram, note } → returns the profile
// with status 'pending'. Requires at least one social handle.
export async function applyCreator({ tiktok, instagram, note } = {}) {
  return api('/creators/apply', {
    method: 'POST',
    body: { tiktok, instagram, note },
  });
}
// Back-compat alias for older callers.
export const submitCreatorApplication = applyCreator;

// GET /creators/briefs → the marketplace briefs (array). Each brief carries the
// current viewer's `creatorState` ('open' | 'applied' | 'not_selected' |
// 'confirmed' | 'in_review' | 'approved').
export async function fetchBriefs() {
  const res = await api('/creators/briefs');
  return Array.isArray(res) ? res : res?.briefs ?? [];
}

// GET /creators/tasks → briefs filtered to confirmed + open (for the countdowns).
export async function fetchTasks() {
  const res = await api('/creators/tasks');
  return Array.isArray(res) ? res : res?.briefs ?? [];
}

// POST /creators/briefs/:id/apply → applies to a brief (creatorState → 'applied').
export async function applyToBrief(briefId) {
  return api(`/creators/briefs/${briefId}/apply`, { method: 'POST' });
}

// POST /creators/briefs/:id/submit → MULTIPART video upload. Field name is
// `files` (multer .array('files')); one File per required slot. Optionally sends
// the slot indexes as a text field. Returns the updated brief.
//
// We use XHR directly (not api()) so the browser sets the multipart boundary
// itself — never set Content-Type manually for FormData — and so upload
// progress can be reported through `onProgress(percent)`. Auth mirrors
// config/api: the raw Authorization header (no "Bearer" prefix).
export function submitBriefVideos(briefId, files, slotIndexes, onProgress) {
  const form = new FormData();
  (files || []).forEach((file) => {
    if (file) form.append('files', file);
  });
  if (Array.isArray(slotIndexes) && slotIndexes.length) {
    form.append('slotIndexes', JSON.stringify(slotIndexes));
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/creators/briefs/${briefId}/submit`);
    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', token);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }

    xhr.onerror = () => reject(new Error('Network error'));
    xhr.onload = () => {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = null;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const err = new Error(data?.message || data?.error || `HTTP ${xhr.status}`);
        err.status = xhr.status;
        err.data = data;
        reject(err);
        return;
      }

      if (data && typeof data === 'object') {
        if ('data' in data && 'success' in data) return resolve(data.data);
        if ('message' in data) return resolve(data.message);
      }
      resolve(data);
    };

    xhr.send(form);
  });
}

// GET /creators/payouts → { currency, available, earnings:[...], history:[...] }.
export async function fetchPayouts() {
  return api('/creators/payouts');
}
// Back-compat alias for older callers.
export const fetchCreatorPayouts = fetchPayouts;
